'use client';

import { ChevronRight } from '@/icons/ChevronRight';
import { CheckoutPage } from '@/lib/checkoutRoutes';
import { HELP_BUTTON_ID } from '@/lib/Constants';
import { isDevelopment } from '@/lib/utils';
import { usePathname } from 'next/navigation';
import Script from 'next/script';
import { useEffect, useMemo, useState } from 'react';
import DailyDeliveryTimer from './DailyDeliveryTimer';
import { Button } from './ui/button';
import WhatsAppChatBubble from './WhatsAppChatBubble';

export default function PageBottomActions({
  helpButtonId,
}: {
  helpButtonId?: number | HELP_BUTTON_ID.WHATSAPP;
}) {
  const pathname = usePathname();
  const [loadChatbot, setLoadChatbot] = useState(false);

  const [showScrollButton, setShowScrollButton] = useState(false);
  const showBottomComponents = useMemo(() => {
    return [...Object.values(CheckoutPage)].filter((item) => pathname.includes(item)).length === 0;
  }, [pathname]);

  useEffect(() => {
    document.documentElement.classList.toggle('hzc-hide-bottom-fabs', !showBottomComponents);
    // Geriye uyumluluk: eski selector ile chat container
    document
      .getElementById('hizlicicek-widget-container')
      ?.classList.toggle('hide-ai-widget', !showBottomComponents);

    return () => document.documentElement.classList.remove('hzc-hide-bottom-fabs');
  }, [showBottomComponents]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollButton(window.scrollY > 200);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle('hzc-scrolled', showScrollButton);
    return () => document.documentElement.classList.remove('hzc-scrolled');
  }, [showScrollButton]);

  // Defer chatbot SDK until idle / interaction so it does not compete with LCP/TBT.
  useEffect(() => {
    if (helpButtonId !== HELP_BUTTON_ID.CHATBOT) return;

    let done = false;
    let idleId: number | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const enable = () => {
      if (done) return;
      done = true;
      cleanup();
      setLoadChatbot(true);
    };

    const events: Array<keyof WindowEventMap> = ['scroll', 'pointerdown', 'keydown', 'touchstart'];

    const cleanup = () => {
      events.forEach((event) => window.removeEventListener(event, enable));
      if (timeoutId) clearTimeout(timeoutId);
      if (idleId !== undefined && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleId);
      }
    };

    events.forEach((event) =>
      window.addEventListener(event, enable, { once: true, passive: true }),
    );

    if ('requestIdleCallback' in window) {
      idleId = window.requestIdleCallback(() => enable(), { timeout: 20_000 });
    }
    timeoutId = setTimeout(enable, 20_000);

    return cleanup;
  }, [helpButtonId]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const shouldShowWhatsAppBubble = useMemo(() => {
    if (helpButtonId === HELP_BUTTON_ID.WHATSAPP) {
      return true;
    }
    return false;
  }, [helpButtonId]);

  const shouldShowChatBotButton = useMemo(() => {
    if (helpButtonId === HELP_BUTTON_ID.CHATBOT) {
      return true;
    }
    return false;
  }, [helpButtonId]);

  if (!showBottomComponents) {
    return null;
  }

  return (
    <>
      {shouldShowChatBotButton && loadChatbot && (
        <Script
          strategy="lazyOnload"
          src={
            isDevelopment
              ? 'https://dev-chatbot.hizli-cicek.com/widget/widget.iife.js?v=2'
              : 'https://chatbot.hizli-cicek.com/widget/widget.iife.js?v=2'
          }
        />
      )}
      {shouldShowWhatsAppBubble && <WhatsAppChatBubble />}
      <div
        className="fixed z-49 flex flex-row justify-between w-full px-4 items-center focus pointer-events-none"
        style={{
          bottom:
            'calc(1.25rem + env(safe-area-inset-bottom, 0px) + var(--hzc-vv-bottom-inset, 0px))',
        }}
      >
        <div className="flex flex-col gap-2">
          <DailyDeliveryTimer className="flex" />
          {/* Sabit yükseklik: scroll eşiğinde layout sıçramasın (anasayfa + ürün) */}
          <div
            className="h-12 w-12 shrink-0 flex items-center justify-center"
            aria-hidden={!showScrollButton}
          >
            {showScrollButton ? (
              <Button
                type="button"
                onClick={scrollToTop}
                className="rounded-full shadow-lg transition-all duration-200 size-12 bg-hzc_green hover:bg-hzc_green hover:scale-105 z-50 pointer-events-auto"
              >
                <ChevronRight className="-rotate-90 text-hzc_beige_100 text-lg" />
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}
